import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Zap, Eye, Flame, Check, Globe } from "lucide-react";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { languageNames } from "@/locales";
import { cn } from "@/lib/utils";

// Desktop Trust Badges Component
const DesktopTrustBadges = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const badges = [
    { icon: Zap, text: "72h", color: "text-neon-cyan", tooltip: "Guaranteed 72-hour delivery" },
    { icon: Eye, text: "850K+", color: "text-white/70", tooltip: "Views generated for clients" },
    { icon: Flame, text: "97%", color: "text-neon-green", tooltip: "97% of clients reorder" },
  ];

  return (
    <div className={cn(
      "hidden lg:flex items-center gap-1 ml-4 transition-all duration-700",
      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
    )}>
      {badges.map((badge, index) => (
        <div
          key={badge.text}
          className="group relative flex items-center gap-1 px-2 py-1 rounded-full 
                     bg-white/5 border border-white/10 hover:border-neon-purple/40
                     transition-all duration-300 hover:bg-white/10 cursor-default desktop-badge-hover"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <badge.icon className={cn("w-3 h-3", badge.color)} />
          <span className={cn("text-[10px] font-bold", badge.color)}>{badge.text}</span>
          
          {/* Tooltip */}
          <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 px-2 py-1 
                          bg-background/95 border border-white/20 rounded text-[10px] text-white/80
                          opacity-0 group-hover:opacity-100 scale-95 group-hover:scale-100
                          transition-all duration-200 whitespace-nowrap pointer-events-none z-50
                          shadow-lg shadow-black/50">
            {badge.tooltip}
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-background/95 
                            border-l border-t border-white/20 rotate-45" />
          </div>
          
          {/* Neon underline on hover */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[1px] 
                          bg-gradient-to-r from-neon-purple to-neon-pink
                          group-hover:w-3/4 transition-all duration-300" />
        </div>
      ))}
    </div>
  );
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const { t, language, setLanguage } = useLanguage();

  useEffect(() => {
    const updateScrollState = () => {
      const scrollPosition = window.scrollY;
      const scrollingDown = scrollPosition > lastScrollY.current;
      const scrollDelta = Math.abs(scrollPosition - lastScrollY.current);
      
      if (scrollDelta > 5) {
        setIsHidden(scrollingDown && scrollPosition > 50);
      }
      
      lastScrollY.current = scrollPosition;
      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(updateScrollState);
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 glass-morphism border-b border-border/50 transition-transform duration-300 ${
        isHidden ? '-translate-y-full' : 'translate-y-0'
      }`} style={{ height: '56px' }}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo + Desktop Trust Badges */}
            <div className="flex items-center">
              <a href="/" className="block tap-feedback magnetic-hover" aria-label="Fuel72 Home">
                <Logo className="h-7 md:h-8 lg:h-9 w-auto" variant="horizontal" />
              </a>
              <DesktopTrustBadges />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <button
                onClick={() => scrollToSection("formats")}
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                {t("nav.howItWorks")}
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                {t("nav.pricing")}
              </button>
              <a
                href="/portfolio"
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                Examples
              </a>
              <button
                onClick={() => scrollToSection("faq")}
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                {t("nav.faq")}
              </button>
              <a
                href="/blog"
                className="text-sm lg:text-base text-muted-foreground hover:text-primary transition-colors font-normal btn-hover-glow"
              >
                Blog
              </a>
            </div>

            {/* CTA Button + Language Toggle */}
            <div className="hidden md:flex items-center gap-3">
              <LanguageToggle />
              <Button
                onClick={() => window.location.href = "/order"}
                className="group bg-gradient-to-r from-orange-500 via-neon-pink to-orange-600 hover:opacity-90 text-white rounded-full font-heading font-semibold text-sm lg:text-base px-6 lg:px-7 h-10 lg:h-11 animate-glow-pulse hover:animate-flame-flicker btn-premium"
              >
                <span className="relative mr-1">
                  <span className="inline-block group-hover:animate-fuel-pump">⛽</span>
                  <span className="fuel-drip-container">
                    <span className="fuel-drip fuel-drip-1"></span>
                    <span className="fuel-drip fuel-drip-2"></span>
                    <span className="fuel-drip fuel-drip-3"></span>
                  </span>
                </span>
                {t("nav.getContent")}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 hover:bg-muted rounded-lg transition-colors tap-feedback"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          <div 
            className={`md:hidden absolute top-full left-0 right-0 pb-4 pt-2 space-y-1 bg-background/95 backdrop-blur-xl border-b border-border/50 px-4 transition-all duration-300 ease-out ${
              isMenuOpen 
                ? 'opacity-100 translate-y-0 pointer-events-auto' 
                : 'opacity-0 -translate-y-2 pointer-events-none'
            }`}
          >
              <button
                onClick={() => scrollToSection("formats")}
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                {t("nav.howItWorks")}
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                ⛽ {t("nav.pricing")}
              </button>
              <a
                href="/portfolio"
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                🎬 Examples
              </a>
              <button
                onClick={() => scrollToSection("faq")}
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                {t("nav.faq")}
              </button>
              <a
                href="/blog"
                className="block w-full text-left px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors font-medium text-base tap-feedback"
              >
                📚 Blog
              </a>
              
              {/* Language Selector in Mobile Menu — inline segmented pills */}
              <div className="px-4 pt-3 pb-1">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    {t("language.change")}
                  </span>
                </div>
                <div className="relative flex items-center gap-1.5 p-1 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-md">
                  {(["en", "de"] as const).map((lang) => {
                    const info = languageNames[lang];
                    const isSelected = language === lang;
                    return (
                      <button
                        key={lang}
                        onClick={() => setLanguage(lang)}
                        className={cn(
                          "relative flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 tap-feedback",
                          isSelected
                            ? "bg-gradient-to-br from-neon-purple/25 to-neon-cyan/15 border border-neon-purple/40 text-foreground shadow-lg shadow-neon-purple/10"
                            : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"
                        )}
                        aria-pressed={isSelected}
                      >
                        <span className="text-lg leading-none">{info.flag}</span>
                        <span className="tracking-tight">{info.nativeName}</span>
                        {isSelected && (
                          <span className="ml-0.5 w-4 h-4 rounded-full bg-neon-purple/25 flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-neon-purple" strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div className="px-4 pt-2">
                <Button
                  onClick={() => window.location.href = "/order"}
                  className="group w-full bg-gradient-to-r from-orange-500 via-neon-pink to-orange-600 hover:opacity-90 text-white rounded-full font-heading font-semibold h-12 text-base animate-glow-pulse tap-feedback"
                >
                  <span className="relative mr-1">
                    <span className="inline-block group-hover:animate-fuel-pump">⛽</span>
                    <span className="fuel-drip-container">
                      <span className="fuel-drip fuel-drip-1"></span>
                      <span className="fuel-drip fuel-drip-2"></span>
                      <span className="fuel-drip fuel-drip-3"></span>
                    </span>
                  </span>
                  {t("nav.getContent")}
                </Button>
              </div>
          </div>
        </div>
      </nav>

      {/* Mobile Trust Anchors Bar - Always visible below navbar */}
      <div 
        className={`md:hidden fixed left-0 right-0 z-40 transition-all duration-300 ${
          isHidden ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100'
        }`}
        style={{ top: '56px' }}
      >
        <div className="bg-background/60 backdrop-blur-md border-b border-white/5 py-1.5">
          <div className="flex items-center justify-center gap-2 text-[10px] animate-trust-bar-entrance">
            <span className="text-neon-cyan font-medium flex items-center gap-1">
              <span className="animate-pulse">⚡</span> 72h
            </span>
            <span className="text-white/30">•</span>
            <span className="text-white/60 flex items-center gap-1">
              👁️ 850K+
            </span>
            <span className="text-white/30">•</span>
            <span className="text-neon-green font-medium flex items-center gap-1">
              🔥 97%
            </span>
            <span className="text-white/30">•</span>
            <span className="text-white/60 flex items-center gap-1">
              🚀 250+
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
